import { OrderMeal, RecentOrFavoriteOrderMeal, PreConfiguredMeal, MenuItemLookupResult, OrderMealEntree, RecentOrFavoriteEntree, RecentOrFavoriteItem, OrderMealItem, PreConfiguredMealDrink, PreConfiguredMealItem, PortionItemType, ItemCustomizationId, GeneralItemType, RecentOrFavoriteEntreeContents, PreConfiguredMealEntreeContent, UpsellItem, MealType, PreConfiguredMealSide } from '@/models';
import { AppState } from '@/store';
import { round, reduce } from 'lodash';
import { Store } from 'vuex';
import { AnalyticsMeal, AnalyticsUpsellType, MerchVars, Product } from '../models';
import { MUTATIONS } from '@/store/adobe';

type Entree = OrderMealEntree | RecentOrFavoriteEntree;
type Item = OrderMealItem | RecentOrFavoriteItem;
enum ProductDetails {
  Normal = 'Normal',
  Double = 'Double',
  Extra = 'Extra',
  Side = 'Side',
}

export class ProductListItem implements Product {
  SKU?: string;
  name?: string;
  priceTotal?: number;
  quantity?: number;
  _merchVars?: MerchVars;

  static getProductListItems(meals: AnalyticsMeal, store: Store<AppState>, isDelivery: boolean, orderPath?: string): Product[] {
    let productListItems: Product[] = [];
    (Array.isArray(meals) ? meals : [meals])?.map(meal => productListItems = [...productListItems, ...ProductListItem.formatProduct(meal, store, isDelivery, orderPath)]);

    return productListItems;
  }

  static formatProduct(meal: OrderMeal | RecentOrFavoriteOrderMeal | PreConfiguredMeal, store: Store<AppState>, isDelivery: boolean, orderPath?: string): Product[] {
    let productListItems = [];
    let product = {
      _merchVars: {
        topThingsChoice: '',
        otherOptions: '',
        includedSides: '',
        drinks: '',
        riceChoice: '',
        beansChoice: ''
      },
    } as Product;
    let menuItemLookupMap = store.state.menu.menuItemLookupMap;
    // When a user selects an upsell item, we dummy up a meal, and throw all sides, drinks, etc under it
    // To know if an item (i.e. chips) is an upsell item, we need to see if the meal it falls under is the upsell meal
    const upsellMealId = store.state.order.upsellMeal?.mealId;
    let isUpsellMeal = upsellMealId ? meal.mealId == upsellMealId : false;

    (meal as OrderMeal | RecentOrFavoriteOrderMeal).entrees?.forEach((entree: OrderMealEntree | RecentOrFavoriteEntree) => {
      if (meal.preconfiguredMealId) {
        const convertedMeal = this.mapMealToPreconfiguredMeal(meal as OrderMeal | RecentOrFavoriteOrderMeal, store, menuItemLookupMap)
        this.formatPceEntree(convertedMeal, menuItemLookupMap, product, isDelivery, store, orderPath);
      } else {
        this.formatEntree(entree, menuItemLookupMap, product, isDelivery, meal, store, orderPath);
      }
    });

    this.formatPceEntree((meal as PreConfiguredMeal), menuItemLookupMap, product, isDelivery, store, orderPath);

    meal.drinks?.forEach((drink: RecentOrFavoriteItem | OrderMealItem | PreConfiguredMealDrink) => {
      let drinkItem = drink as RecentOrFavoriteItem | OrderMealItem;
      let pceDrinkItem = drink as PreConfiguredMealDrink;

      if (!product.priceTotal) {
        product.priceTotal = 0;
      }

      this.setProductAddMethodForSideOrDrink(drinkItem, product, meal, store, orderPath);

      if (product && product._merchVars) {
        const item = menuItemLookupMap?.get(drinkItem.menuItemId ?? pceDrinkItem.itemId);
        product._merchVars.drinks += `${product._merchVars.drinks ? '~' : ''}${drinkItem.menuItemName ?? pceDrinkItem.itemName}`;

        const drinkProduct = {
          SKU: drinkItem.menuItemId ?? pceDrinkItem.itemId,
          name: drinkItem.menuItemName ?? pceDrinkItem.itemName,
          productAddMethod: product.productAddMethod ?? orderPath,
          quantity: drinkItem.quantity,
          priceTotal: ((isDelivery ? item?.unitDeliveryPrice : item?.unitPrice) ?? 0) * drinkItem.quantity,
          _merchVars: {
            pceName: product._merchVars.pceName
          } as MerchVars
        };

        this.formatUpsellData(drinkProduct, isUpsellMeal, store.state.adobeTarget.displayedUpsellItems, drinkItem.menuItemId ?? pceDrinkItem.itemId);

        productListItems.push(drinkProduct);
      }
    });

    const extraProteinIds = store.state.menu.metadata?.itemSections['ExtraProteinSideContentGroup']?.items?.map(i => i.menuItemId) ?? [];

    meal.sides?.forEach((side: RecentOrFavoriteItem | OrderMealItem | PreConfiguredMealItem) => {
      let sideItem = side as RecentOrFavoriteItem | OrderMealItem;
      let pceSideItem = side as PreConfiguredMealItem;
      if (meal.preconfiguredMealId){
        const convertedMeal = this.mapMealToPreconfiguredMeal(meal as OrderMeal | RecentOrFavoriteOrderMeal, store, menuItemLookupMap)
        this.formatPcmSide(side as PreConfiguredMealSide, convertedMeal, product)
      }
      this.formatPcmSide(side as PreConfiguredMealSide, meal as PreConfiguredMeal, product)

      if (!product.priceTotal) {
        product.priceTotal = 0;
      }

      this.setProductAddMethodForSideOrDrink(sideItem, product, meal, store, orderPath);

      if (product && product._merchVars) {
        const item = menuItemLookupMap?.get(sideItem?.menuItemId ?? pceSideItem?.itemId);
        product._merchVars.includedSides += `${product._merchVars.includedSides ? '~' : ''}${sideItem?.menuItemName ?? pceSideItem?.itemName}`;

        const sideProduct = {
          SKU: sideItem.menuItemId ?? pceSideItem.itemId,
          name: sideItem.menuItemName ?? pceSideItem.itemName,
          productAddMethod: product.productAddMethod ?? orderPath,
          quantity: sideItem.quantity ?? pceSideItem.quantity,
          priceTotal: ((isDelivery ? item?.unitDeliveryPrice : item?.unitPrice) ?? 0) * sideItem.quantity,
          _merchVars: {
            pceName: product._merchVars.pceName,
            proteinChoice: extraProteinIds.includes(sideItem.menuItemId ?? pceSideItem.itemId) ? `${(sideItem.menuItemName ?? pceSideItem.itemName).replace('Side of ', '')}~${ProductDetails.Side}` : undefined
          } as MerchVars
        };

        this.formatUpsellData(sideProduct, isUpsellMeal, store.state.adobeTarget.displayedUpsellItems, sideItem.menuItemId ?? pceSideItem.itemId);

        productListItems.push(sideProduct);
      }
    });

    product.priceTotal = round(product.priceTotal ?? 0, 2);

    if (product.SKU) {
      productListItems.push(product);
      this.formatUpsellData(product, isUpsellMeal, store.state.adobeTarget.displayedUpsellItems, product.SKU);
    }

    return productListItems;
  }

  private static formatPcmSide(side: PreConfiguredMealSide, meal: PreConfiguredMeal, product: Product){
    // Only format if PCM is a side with NO entrees. 
    // Check if a side is a PCM using the sideName value
    const entree = meal?.entree
    if (!meal || entree || (side.sideName == undefined && !meal.preconfiguredMealId)){
      return;
    } 

    if (product._merchVars) {
      product._merchVars.pceName = meal.preconfiguredMealName ?? meal.mealName
    }
  }

  private static formatPceEntree(meal: PreConfiguredMeal, menuItemLookupMap: Map<string, MenuItemLookupResult> | null, product: Product, isDelivery: boolean, store: Store<AppState>, orderPath?: string) {
    const entree = meal?.entree;
    if (!meal || !entree) {
      return;
    }

    const menuItem = menuItemLookupMap?.get(entree.itemId);

    // set up product props
    product.SKU = entree.itemId ?? '';
    product.name = entree.itemName;
    product.quantity = 1;

    if (product._merchVars) {
      product._merchVars.pceName = meal.mealName;
    }

    const productAddMethod = store.state.adobe.productAddMethodList.find(method => method.mealId == (meal.preconfiguredMealId ?? meal.mealId));
  
    if (productAddMethod) {
      product.productAddMethod = productAddMethod.method;
    } else if (orderPath) {
      store.commit(MUTATIONS.SET_PRODUCT_ADD_METHOD, { mealId: meal.mealId, method: orderPath });
      product.productAddMethod = orderPath;
    } 

    product.priceTotal =
      ((isDelivery ? menuItem?.unitDeliveryPrice : menuItem?.unitPrice) ?? 0) +
      reduce(
        entree.contents,
        (a, b) => {
          const price = isDelivery ? menuItemLookupMap?.get(b.itemId)?.unitDeliveryPrice : menuItemLookupMap?.get(b.itemId)?.unitPrice;
          return a + (price ?? 0);
        },
        0,
      );

    // Format Primary filling portion
    const portion = entree.contents?.find(
      item =>
        menuItemLookupMap?.get(item.itemId)?.menuItemType == PortionItemType.HalfPortion || menuItemLookupMap?.get(item.itemId)?.menuItemType == PortionItemType.ExtraPortion,
    );
    const portionType = menuItemLookupMap?.get(portion?.itemId ?? '')?.menuItemType ?? '';
    const extraProtein = entree.contents?.find(item => menuItemLookupMap?.get(item.itemId)?.menuItemType === 'ExtraProtein')?.itemName;
    this.formatPrimaryFillingPortion(portionType, portion, product, menuItem?.primaryFillingName ?? '', portion?.itemName, extraProtein);

    // Format Entree Contents
    entree.contents?.forEach(content => {
      const menuItemType = menuItemLookupMap?.get(content.itemId)?.menuItemType ?? '';
      const customId = content.customizationId;
      this.formatEntreeContents(product, content.itemName, content.itemId, menuItemType, customId);
    });
  }

  private static formatEntree(entree: Entree, menuItemLookupMap: Map<string, MenuItemLookupResult> | null, product: Product, isDelivery: boolean, meal: OrderMeal | RecentOrFavoriteOrderMeal | PreConfiguredMeal, store: Store<AppState>, orderPath?: string) {
    if (!entree) {
      return;
    }

    const menuItem = menuItemLookupMap?.get(entree.menuItemId);

    // Set up product properties
    product.SKU = entree.menuItemId ?? '';
    product.name = entree.menuItemName || menuItem?.menuItemName;
    product.quantity = 1;

    if (product._merchVars && meal.preconfiguredMealId) {
      product._merchVars.pceName = meal.preconfiguredMealName;
    }

    const productAddMethod = store.state.adobe.productAddMethodList.find(method => method.mealId == meal.mealId);

    if (orderPath) {
      store.commit(MUTATIONS.SET_PRODUCT_ADD_METHOD, { mealId: meal.mealId, method: orderPath });
      product.productAddMethod = orderPath;
    } else if (productAddMethod) {
      product.productAddMethod = productAddMethod.method;
    }

    product.priceTotal =
      ((isDelivery ? menuItem?.unitDeliveryPrice : menuItem?.unitPrice) ?? 0) +
      reduce(
        entree.contents,
        (a, b) => {
          const price = isDelivery ? menuItemLookupMap?.get(b.menuItemId)?.unitDeliveryPrice : menuItemLookupMap?.get(b.menuItemId)?.unitPrice;
          return a + (price ?? 0);
        },
        0,
      );

    // Format portion size of primary filling
    const portion = entree.contents?.find(
      item =>
        menuItemLookupMap?.get(item.menuItemId)?.menuItemType == PortionItemType.HalfPortion ||
        menuItemLookupMap?.get(item.menuItemId)?.menuItemType == PortionItemType.ExtraPortion,
    );
    const portionType = menuItemLookupMap?.get(portion?.menuItemId ?? '')?.menuItemType ?? '';
    const extraProtein = entree.contents?.find(item => menuItemLookupMap?.get(item.menuItemId)?.menuItemType === 'ExtraProtein')?.menuItemName;
    this.formatPrimaryFillingPortion(portionType, portion, product, menuItem?.primaryFillingName ?? '', portion?.menuItemName, extraProtein);

    // Format all entree contents
    entree.contents?.forEach(content => {
      const menuItemType = menuItemLookupMap?.get(content.menuItemId)?.menuItemType ?? '';
      const customId = content.customizationId;
      this.formatEntreeContents(product, content.menuItemName, content.menuItemId, menuItemType, customId);
    });
  }

  private static setProductAddMethodForSideOrDrink(item: Item, product: Product, meal: OrderMeal | RecentOrFavoriteOrderMeal | PreConfiguredMeal, store: Store<AppState>, orderPath?: string) {
    if (!item) {
      return;
    }

    // Get mealId for meals that don't have an entree.
    let mealId: string;
    if ((meal as OrderMeal | RecentOrFavoriteOrderMeal).entrees?.length == 0 || ('entree' in meal && meal.entree == null)) {
      mealId = meal.preconfiguredMealId || meal.mealId;
    } else {
      return;
    }

    const productAddMethod = store.state.adobe.productAddMethodList.find(method => method.mealId == mealId);
    
    if (orderPath) {
      store.commit(MUTATIONS.SET_PRODUCT_ADD_METHOD, { mealId: mealId, method: orderPath });
      product.productAddMethod = orderPath;
    } else if (productAddMethod) {
      product.productAddMethod = productAddMethod.method;
    }
  }

  private static formatEntreeContents(product: Product, menuItemName: string, menuItemId: string, menuItemType: string, customizationId: number) {
    const customId = customizationId;
    const customType = ItemCustomizationId[customId];
    product._merchVars = product._merchVars ?? {};

    switch (menuItemType) {
      case GeneralItemType.Rice:
        if (!customId) {
          product._merchVars.riceChoice = `${menuItemName}~${ProductDetails.Normal}`;
        } else {
          product._merchVars.riceChoice += `${product._merchVars.riceChoice ? '~' : ''}${menuItemName}${customId == ItemCustomizationId.Half ? '-1/2' : '~' + customType}${product._merchVars.riceChoice ? '~Half Rice' : ''}`;
        }
        break;
      case GeneralItemType.Beans:
        if (!customId) {
          product._merchVars.beansChoice = `${menuItemName}~${ProductDetails.Normal}`;
        } else {
          product._merchVars.beansChoice += `${product._merchVars.beansChoice ? '~' : ''}${menuItemName}${customId == ItemCustomizationId.Half ? '-1/2' : '~' + customType}${product._merchVars.beansChoice ? '~Half Beans' : ''}`;
        }
        break;
      case GeneralItemType.Toppings:
      case GeneralItemType.Addon:
        product._merchVars.topThingsChoice += `${product._merchVars.topThingsChoice ? '~' : ''}${customId ? menuItemName + ':' + customType : menuItemName + ':' + ProductDetails.Normal
          }`;
        break;
      case GeneralItemType.Option:
        product._merchVars.otherOptions += `${product._merchVars.otherOptions ? '~' : ''}${menuItemName}`;
        break;
      // Kid items have side and beverage as entree contents
      case GeneralItemType.Side:
        product._merchVars.includedSides += `${product._merchVars.includedSides ? '~' : ''}${menuItemName}`;
        break;
      case GeneralItemType.Beverage:
        product._merchVars.drinks += `${product._merchVars.drinks ? '~' : ''}${menuItemName}`;
        break;
      case GeneralItemType.Tortillas:
        product._merchVars.otherOptions += `${product._merchVars.otherOptions ? '~' : ''}${menuItemName}`;
    }
  }

  private static formatPrimaryFillingPortion(
    portionType: string,
    portion: RecentOrFavoriteEntreeContents | PreConfiguredMealEntreeContent | undefined,
    product: Product,
    primaryFillingName: string,
    portionName: string | undefined,
    extraProtein?: string | undefined,
  ) {
    if (!product._merchVars) {
      return;
    }

    if (!portion) {
      if (extraProtein) {
        product._merchVars.proteinChoice = `${primaryFillingName}~${ProductDetails.Normal};${extraProtein.replace('Extra ', '')}~${ProductDetails.Extra}`;
      } else {
        product._merchVars.proteinChoice = `${primaryFillingName}~${ProductDetails.Normal}`;
      }
    } else if (portionType == PortionItemType.HalfPortion) {
      product._merchVars.proteinChoice = `1/2 ${primaryFillingName} + 1/2 ${portionName?.replace('Half', '').trim()}~${ProductDetails.Normal}`;
    } else if (portionType == PortionItemType.ExtraPortion) {
        if ((portionName?.replace('Extra ', '').trim()) == primaryFillingName) {
          product._merchVars.proteinChoice = `${primaryFillingName}~${ProductDetails.Double}`;
        } else {
          product._merchVars.proteinChoice = `${primaryFillingName} + ${portionName?.replace('Extra ', '')}~${ProductDetails.Double}`;
        }
    }
  }

  private static formatUpsellData(product: Product, isUpsellMeal: boolean, upsellItems: UpsellItem[], cmgId: string) {
    if (product._merchVars) {
      if (isUpsellMeal) {
        if (upsellItems.length > 0) {
          const matchingUpsellItem = upsellItems.find(i => i.cmgId == cmgId);
          product._merchVars.upsellType = matchingUpsellItem?.upsellType ?? AnalyticsUpsellType.Static;
        } else {
          product._merchVars.upsellType = AnalyticsUpsellType.Static;
        }
        product._merchVars.upsellAmount = product.priceTotal;
      } else {
        product._merchVars.upsellType = AnalyticsUpsellType.NotUpsell;
      }
    }
  }

  private static mapMealToPreconfiguredMeal(meal: OrderMeal | RecentOrFavoriteOrderMeal, store: Store<AppState>, menuItemLookupMap: Map<string, MenuItemLookupResult> | null): PreConfiguredMeal {

    let mealEntree;
    if (meal?.entrees && meal.entrees.length > 0) {
      mealEntree = meal.entrees[0] as OrderMealEntree | RecentOrFavoriteEntree;
    }
    const pce = store.state.menu.preConfiguredMeals.find(p => p.mealId === meal.preconfiguredMealId);
    return {
      preconfiguredMealId: meal.preconfiguredMealId,
      preconfiguredMealName: meal.preconfiguredMealName,
      mealId: meal.mealId,
      mealName: meal.preconfiguredMealName ?? meal.mealName,
      mealType: pce?.mealType ?? '' as MealType,
      dietaryTags: [],
      mealTags: [],
      primaryImages: [],
      secondaryImages: [],
      mealPrice: 0,
      mealDeliveryPrice: 0,
      description: pce?.description ?? '',

      entree: {
        calories: '',
        entreeName: meal?.mealName ?? '',
        itemId: mealEntree?.menuItemId ?? '',
        itemName: mealEntree?.menuItemName ?? '',
        quantity: mealEntree?.quantity ?? 1,
        unitPrice: 0,
        isItemAvailable: true,
        contents: mealEntree?.contents?.map(content => ({
          itemId: content.menuItemId,
          itemName: content.menuItemName,
          quantity: content.quantity,
          customizationId: content.customizationId,
          customizationName: '',
          isItemAvailable: true,
          unitPrice: 0,
        })) ?? [],
      },
      sides: [],
      drinks: [],
    }
  }
}